import * as React from 'react';

import { c } from 'ttag';

import { ChevronLeft, ChevronRight } from '@freelancelabs/icons';
import { clsx } from '@freelancelabs/utils';

import Button from '../button/Button';
import SelectNative from '../select/SelectNative';

/** A gap in the numbered buttons, rendered as an ellipsis rather than a page. */
const GAP = 'gap' as const;

type PageSlot = number | typeof GAP;

export interface PaginationProps {
    /** Current page, **1-based**: it is the number the reader sees. */
    page: number;
    pageSize: number;
    /**
     * How many rows the whole list holds, or `null` when the API does not say.
     *
     * `null` is not zero. An API that paginates without a total cannot tell how many
     * pages there are, so the pager stops numbering them and offers « previous » and
     * « next » only: printing a page count it does not know would be an invention.
     */
    totalCount: number | null;
    /** Consulted only when `totalCount` is null: the sole clue that a next page exists. */
    hasNextPage?: boolean;
    onPageChange: (page: number) => void;
    /** Omit to fix the page size: the rows-per-page control then disappears. */
    onPageSizeChange?: (pageSize: number) => void;
    pageSizes?: number[];
    /**
     * What the numbers count, in the caller's own words.
     *
     * Deliberately not built here. « 1–25 sur 1 972 » is arithmetic, but the noun
     * that follows carries a business rule: a list that shows only the rows still in
     * progress has to say so, and the design system has no way to know that.
     */
    summary?: React.ReactNode;
    className?: string;
}

const DEFAULT_PAGE_SIZES = [10, 25, 50];

/**
 * Which page buttons to draw.
 *
 * The mockup loops over every page, which is a demo talking about fourteen rows: on
 * a real perimeter the same loop draws eighty buttons. The window keeps the two ends
 * (« first » and « last » are the jumps a reader actually makes) plus the
 * neighbours of the current page, and marks the rest as a gap.
 */
const getPageSlots = (page: number, pageCount: number): PageSlot[] => {
    if (pageCount <= 7) {
        return Array.from({ length: pageCount }, (_, index) => index + 1);
    }

    const around = [page - 1, page, page + 1].filter((value) => value > 1 && value < pageCount);
    const slots: PageSlot[] = [1];

    if (around.length === 0 || around[0] > 2) {
        slots.push(GAP);
    }
    slots.push(...around);
    if (around.length === 0 || around[around.length - 1] < pageCount - 1) {
        slots.push(GAP);
    }
    slots.push(pageCount);

    return slots;
};

/**
 * Pager of a list paginated by `limit`/`offset`.
 *
 * Rendered next to the table rather than inside it, like `RegionState`: the scroll
 * container is as wide as the widest row, so anything laid out inside it drifts
 * off-screen sideways.
 *
 * The component holds no state. Page and page size belong to whoever owns the query
 *: a pager that remembered its own page would disagree with the rows on screen the
 * first time the caller reset it.
 */
const Pagination = ({
    page,
    pageSize,
    totalCount,
    hasNextPage = false,
    onPageChange,
    onPageSizeChange,
    pageSizes = DEFAULT_PAGE_SIZES,
    summary,
    className,
}: PaginationProps) => {
    const pageCount = totalCount === null ? null : Math.max(1, Math.ceil(totalCount / pageSize));
    const isLast = pageCount === null ? !hasNextPage : page >= pageCount;

    /*
     * La navigation disparait quand il n'y a qu'une page, le selecteur et le
     * compteur restent : « 1-14 sur 14 » repond a une question, un bouton « 1 »
     * entre deux chevrons eteints n'offre aucun geste.
     *
     * Attention, ELLE RESTE QUAND LE TOTAL EST INCONNU : sans total, « precedent »
     * et « suivant » sont le seul moyen de savoir s'il y a une suite, et la
     * masquer enfermerait le lecteur sur la premiere page.
     *
     * A ne pas confondre avec la regle de l'appelant, qui decide si la BARRE
     * existe (pas de pageur sous une liste vide, sous un echec, ni sous un
     * accordeon qui tient sur une page, ou la maquette efface la barre entiere).
     * Ici c'est la NAVIGATION seule, et les deux regles se cumulent sans se
     * repeter.
     */
    const hasNavigation = pageCount === null || pageCount > 1;

    const previousLabel = c('Pagination').t`Previous page`;
    const nextLabel = c('Pagination').t`Next page`;

    return (
        <nav className={clsx('fl-pagination', className)} aria-label={c('Pagination').t`Table pagination`}>
            {onPageSizeChange && (
                // A wrapping label rather than an aria-label: the visible words and
                // the accessible name are then the same string, and clicking them
                // opens the select.
                <label className="fl-pagination-size">
                    {c('Pagination').t`Rows per page`}
                    <SelectNative
                        className="fl-pagination-size-select"
                        value={pageSize}
                        onValue={(value: number) => onPageSizeChange(Number(value))}
                        options={pageSizes.map((size) => ({ value: size, label: String(size) }))}
                    />
                </label>
            )}

            {/* Polite rather than assertive: the reader triggered the change, so the
                new range is a confirmation, not an interruption. */}
            {summary && (
                <span className="fl-pagination-summary" aria-live="polite">
                    {summary}
                </span>
            )}

            {hasNavigation && (
                <div className="fl-pagination-pages">
                    <Button
                        category="weak"
                        size="small"
                        icon
                        disabled={page <= 1}
                        aria-label={previousLabel}
                        title={previousLabel}
                        onClick={() => onPageChange(page - 1)}
                    >
                        <ChevronLeft size={12} />
                    </Button>

                    {pageCount !== null &&
                        getPageSlots(page, pageCount).map((slot, index) =>
                            slot === GAP ? (
                                <span key={`gap-${index}`} className="fl-pagination-gap" aria-hidden="true">
                                    …
                                </span>
                            ) : (
                                <Button
                                    key={slot}
                                    category="weak"
                                    size="small"
                                    aria-label={c('Pagination').t`Page ${slot}`}
                                    aria-current={slot === page ? 'page' : undefined}
                                    onClick={() => onPageChange(slot)}
                                >
                                    {slot}
                                </Button>
                            )
                        )}

                    <Button
                        category="weak"
                        size="small"
                        icon
                        disabled={isLast}
                        aria-label={nextLabel}
                        title={nextLabel}
                        onClick={() => onPageChange(page + 1)}
                    >
                        <ChevronRight size={12} />
                    </Button>
                </div>
            )}
        </nav>
    );
};

export default Pagination;
